import { useState, useMemo } from 'react';

interface LabResultsViewProps {
  healthStats: any[];
  onAddLabReport?: () => void;
  onDeleteReport?: (statId: string) => void;
}

interface LabTest {
  test: string;
  value?: number | string;
  unit?: string;
  referenceRange?: string;
  flag?: string;
}

interface LabReport {
  id: string;
  date: string;
  panelName: string;
  tests: LabTest[];
  summary?: string;
  abnormalCount: number;
  totalTests: number;
}

const card = (children: React.ReactNode, style?: React.CSSProperties) => (
  <div style={{
    background: 'var(--color-surface)', borderRadius: 12,
    padding: '14px 16px', marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    ...style,
  }}>
    {children}
  </div>
);

const sectionHeader = (title: string) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 10 }}>
    {title}
  </div>
);

export default function LabResultsView({ healthStats, onAddLabReport, onDeleteReport }: LabResultsViewProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const reports = useMemo<LabReport[]>(() => {
    const labStats = healthStats.filter((s: any) => {
      const source = s.source || s.attributes?.source || '';
      const category = s.category || s.attributes?.category || '';
      return source === 'health:labs' || category === 'labs' || category === 'lab_results';
    });
    return labStats
      .sort((a: any, b: any) => {
        const dateA = a.date || a.attributes?.date || a.createdAt || '';
        const dateB = b.date || b.attributes?.date || b.createdAt || '';
        return dateB.localeCompare(dateA);
      })
      .map((s: any, idx: number) => {
        const date = (s.date || s.attributes?.date || s.createdAt || '').slice(0, 10);
        const tests: LabTest[] = s.labResults || s.attributes?.labResults || s.data?.labResults || [];
        const summary = s.summary || s.attributes?.summary || s.data?.summary || '';
        const abnormalCount = tests.filter(t => t.flag && t.flag !== 'normal').length;
        const panelName = s.panelName || s.attributes?.panelName || s.data?.panelName || s.title || s.label || '';
        const cleanName = panelName.startsWith('Lab Results') ? '' : panelName;
        return { id: s.id || `${date}:${cleanName || idx}`, date, panelName: cleanName, tests, summary, abnormalCount, totalTests: tests.length };
      });
  }, [healthStats]);

  const selected = selectedId ? reports.find(r => r.id === selectedId) : null;

  const trends = useMemo(() => {
    if (reports.length < 2) return null;
    const testHistory: Record<string, { date: string; value: number }[]> = {};
    for (const report of reports) {
      for (const test of report.tests) {
        const val = typeof test.value === 'number' ? test.value : parseFloat(String(test.value));
        if (isNaN(val)) continue;
        const key = test.test.toLowerCase().trim();
        if (!testHistory[key]) testHistory[key] = [];
        testHistory[key].push({ date: report.date, value: val });
      }
    }
    const changes: { test: string; prev: number; current: number; direction: 'up' | 'down'; favorable: boolean; pctChange: number; unit: string; context: string }[] = [];
    for (const [key, points] of Object.entries(testHistory)) {
      if (points.length < 2) continue;
      const sorted = [...points].sort((a, b) => b.date.localeCompare(a.date));
      const current = sorted[0].value;
      const prev = sorted[1].value;
      if (prev === 0 || current === prev) continue;
      const pctChange = Math.abs((current - prev) / prev * 100);
      if (pctChange < 3) continue;
      let originalName = key;
      let unit = '';
      for (const report of reports) {
        const found = report.tests.find(t => t.test.toLowerCase().trim() === key);
        if (found) { originalName = found.test; unit = found.unit || ''; break; }
      }
      const direction = current > prev ? 'up' as const : 'down' as const;
      const { context, favorable } = getTestContext(key, direction);
      changes.push({
        test: originalName, prev, current, direction, favorable,
        pctChange: Math.round(pctChange), unit, context,
      });
    }
    changes.sort((a, b) => b.pctChange - a.pctChange);
    return changes.length > 0 ? changes.slice(0, 5) : null;
  }, [reports]);

  if (reports.length === 0) {
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--color-text-tertiary)' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, background: 'rgba(14, 165, 233, 0.08)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>
            No lab results yet
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 260, margin: '0 auto' }}>
            Upload a photo of your lab report or describe your results
          </div>
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <div>
        <button
          onClick={() => setSelectedId(null)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 10,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12, fontWeight: 500, color: '#0ea5e9',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          All lab reports
        </button>

        {card(
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 2 }}>
              {formatLabDate(selected.date)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 12 }}>
              {selected.panelName || 'Lab Report'}
              {selected.totalTests > 0 && <span> &middot; {selected.totalTests} tests</span>}
              {selected.abnormalCount > 0 && <span> &middot; {selected.abnormalCount} flagged</span>}
            </div>

            {selected.summary && (
              <div style={{
                padding: '10px 12px', marginBottom: 12, borderRadius: 8,
                borderLeft: '3px solid #0ea5e9', background: 'rgba(14,165,233,0.04)',
              }}>
                {sectionHeader('Summary')}
                <div style={{ fontSize: 12, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>
                  {selected.summary}
                </div>
              </div>
            )}

            {selected.tests.length > 0 ? (
              <div>
                <div style={{
                  display: 'flex', gap: 8, padding: '0 4px 6px',
                  borderBottom: '1px solid var(--color-border, #e5e7eb)',
                  fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  <span style={{ flex: 2 }}>Test</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Result</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Reference</span>
                  <span style={{ width: 50, textAlign: 'center' }}>Flag</span>
                </div>
                {selected.tests.map((test, i) => {
                  const isAbnormal = test.flag === 'high' || test.flag === 'low' || test.flag === 'abnormal';
                  return (
                    <div key={i} style={{
                      display: 'flex', gap: 8, padding: '7px 4px', alignItems: 'center',
                      borderBottom: i < selected.tests.length - 1 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
                    }}>
                      <span style={{
                        flex: 2, fontSize: 12, fontWeight: 500,
                        color: isAbnormal ? '#f59e0b' : 'var(--color-text-primary)',
                      }}>
                        {test.test}
                      </span>
                      <span style={{
                        flex: 1, fontSize: 12, fontWeight: 600, textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: isAbnormal ? '#f59e0b' : 'var(--color-text-primary)',
                      }}>
                        {test.value !== undefined ? test.value : '—'}
                        {test.unit && <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}> {test.unit}</span>}
                      </span>
                      <span style={{ flex: 1, fontSize: 11, textAlign: 'right', color: 'var(--color-text-tertiary)' }}>
                        {test.referenceRange || '—'}
                      </span>
                      <span style={{ width: 50, textAlign: 'center' }}>
                        {test.flag ? (
                          <span style={{
                            fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                            padding: '1px 5px', borderRadius: 3,
                            background: isAbnormal ? 'rgba(245,158,11,0.10)' : 'rgba(34,197,94,0.08)',
                            color: isAbnormal ? '#f59e0b' : '#22c55e',
                          }}>
                            {test.flag}
                          </span>
                        ) : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 12 }}>
                No individual test data extracted
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {trends && card(
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            Lab Analysis
          </div>
          {(() => {
            const concerning = trends.filter(t => !t.favorable);
            const improving = trends.filter(t => t.favorable);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {concerning.length === 0 && improving.length > 0 && (
                  <div style={{ fontSize: 13, color: '#22c55e', fontWeight: 500 }}>Everything is moving in the right direction.</div>
                )}
                {concerning.length === 0 && improving.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No major changes between your last two reports.</div>
                )}
                {improving.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>Getting better</div>
                    {improving.map((t, i) => (
                      <div key={i} style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                        {friendlyTestName(t.test)} — {t.context || 'improved'}
                      </div>
                    ))}
                  </div>
                )}
                {concerning.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', marginBottom: 4 }}>Worth watching</div>
                    {concerning.map((t, i) => (
                      <div key={i} style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                        {friendlyTestName(t.test)} went {t.direction} {t.pctChange}% — {t.context || 'mention it to your doctor next visit'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
      {reports.map((report) => (
        <div
          key={report.id}
          onClick={() => setSelectedId(report.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(report.id); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 12, background: 'var(--color-surface)', cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            marginBottom: 4,
          }}
        >
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: 'rgba(14,165,233,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {formatLabDate(report.date)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
              {report.panelName || 'Lab Report'}
              {report.totalTests > 0 && <span> &middot; {report.totalTests} tests</span>}
            </div>
          </div>
          {report.abnormalCount > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: '#f59e0b',
              background: 'rgba(245,158,11,0.08)', padding: '2px 7px', borderRadius: 4, flexShrink: 0,
            }}>
              {report.abnormalCount} flagged
            </span>
          )}
          {onDeleteReport && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteReport(report.id); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                color: '#d1d5db', fontSize: 14, lineHeight: 1, flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
              title="Delete report"
            >&#x2715;</button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      ))}
    </div>
  );
}

function formatLabDate(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/** Translate technical test names into plain English */
function friendlyTestName(name: string): string {
  const friendly: Record<string, string> = {
    'egfrcr ckd-epi': 'Kidney function',
    'egfr': 'Kidney function',
    'egfrcr': 'Kidney function',
    'creatinine': 'Creatinine (kidney)',
    'bun/creatinine': 'BUN/Creatinine ratio',
    'blood urea nitrogen': 'BUN (kidney waste)',
    'hemoglobin a1c': 'A1C (blood sugar avg)',
    'hba1c': 'A1C (blood sugar avg)',
    'total cholesterol': 'Total cholesterol',
    'ldl cholesterol': 'LDL ("bad") cholesterol',
    'ldl': 'LDL ("bad") cholesterol',
    'hdl cholesterol': 'HDL ("good") cholesterol',
    'hdl': 'HDL ("good") cholesterol',
    'high-density lipoprotein': 'HDL ("good") cholesterol',
    'low-density lipoprotein': 'LDL ("bad") cholesterol',
    'very low-density lipoprotein': 'VLDL cholesterol',
    'white blood cell count': 'White blood cells (immune)',
    'white blood cells': 'White blood cells (immune)',
    'wbc': 'White blood cells (immune)',
    'red blood cell count': 'Red blood cells',
    'mean corpuscular volume': 'Red blood cell size',
    'mean corpuscular hemoglobin': 'Hemoglobin per cell',
    'mean corpuscular hemoglobin concentration': 'Hemoglobin concentration',
    'red blood cell distribution width': 'Red cell variation',
    'platelet count': 'Platelets (clotting)',
    'alanine aminotransferase': 'ALT (liver)',
    'aspartate aminotransferase': 'AST (liver)',
    'alkaline phosphatase': 'ALP (liver/bone)',
    'bilirubin, total': 'Bilirubin (liver)',
    'bilirubin': 'Bilirubin (liver)',
    'carbon dioxide': 'CO2 (acid/base balance)',
    'protein, total': 'Total protein',
    'globulin (calculated)': 'Globulin (immune protein)',
    'c-reactive protein': 'CRP (inflammation)',
    'crp': 'CRP (inflammation)',
    'prostate specific antigen': 'PSA (prostate)',
    'psa': 'PSA (prostate)',
    'tsh': 'TSH (thyroid)',
  };
  const key = name.toLowerCase().trim();
  return friendly[key] || name;
}

/** Brief plain-language context for common lab test trends. */
function getTestContext(testKey: string, direction: 'up' | 'down'): { context: string; favorable: boolean } {
  const UP_IS_GOOD = new Set(['hdl cholesterol', 'hdl', 'high-density lipoprotein', 'egfr', 'egfrcr ckd-epi', 'egfrcr', 'hemoglobin', 'vitamin d', 'iron']);
  const AMBIGUOUS = new Set(['tsh', 'platelets', 'platelet count', 'ferritin', 'psa', 'prostate specific antigen', 'white blood cells', 'wbc', 'white blood cell count']);

  const ctx: Record<string, { up: string; down: string }> = {
    'total cholesterol': { up: 'higher is harder on your heart', down: 'moving in a heart-healthy direction' },
    'ldl cholesterol': { up: 'can lead to clogged arteries over time', down: 'less buildup risk — nice' },
    'ldl': { up: 'can lead to clogged arteries over time', down: 'less buildup risk — nice' },
    'low-density lipoprotein': { up: 'can lead to clogged arteries over time', down: 'less buildup risk — nice' },
    'hdl cholesterol': { up: 'the "good" kind — helps protect your heart', down: 'less heart protection' },
    'hdl': { up: 'the "good" kind — helps protect your heart', down: 'less heart protection' },
    'high-density lipoprotein': { up: 'the "good" kind — helps protect your heart', down: 'less heart protection' },
    'triglycerides': { up: 'linked to heart and metabolic issues', down: 'healthier metabolic balance' },
    'glucose': { up: 'blood sugar creeping up', down: 'blood sugar improving' },
    'fasting glucose': { up: 'blood sugar creeping up', down: 'blood sugar improving' },
    'hemoglobin a1c': { up: 'average blood sugar has been higher lately', down: 'blood sugar control improving' },
    'hba1c': { up: 'average blood sugar has been higher lately', down: 'blood sugar control improving' },
    'uric acid': { up: 'can trigger gout flares or kidney stones', down: 'lower gout risk' },
    'creatinine': { up: 'kidneys may be working a bit harder', down: 'kidney function looks better' },
    'egfr': { up: 'kidneys filtering better', down: 'kidney filtration may be slowing' },
    'egfrcr ckd-epi': { up: 'kidneys filtering better', down: 'kidney filtration may be slowing' },
    'egfrcr': { up: 'kidneys filtering better', down: 'kidney filtration may be slowing' },
    'alt': { up: 'liver may be under some stress', down: 'liver numbers improving' },
    'alanine aminotransferase': { up: 'liver may be under some stress', down: 'liver numbers improving' },
    'ast': { up: 'could be liver or muscle related', down: 'liver markers improving' },
    'aspartate aminotransferase': { up: 'could be liver or muscle related', down: 'liver markers improving' },
    'alkaline phosphatase': { up: 'could be liver or bone related', down: 'usually not a concern' },
    'tsh': { up: 'thyroid may be slowing down', down: 'thyroid may be overactive' },
    'vitamin d': { up: 'great for bones and immune health', down: 'may affect energy and bone strength' },
    'iron': { up: 'watch for too much iron', down: 'low iron can cause tiredness' },
    'ferritin': { up: 'could mean inflammation or excess iron', down: 'iron stores are low' },
    'hemoglobin': { up: 'blood is carrying oxygen well', down: 'may feel more tired — could be anemia' },
    'white blood cells': { up: 'your body may be fighting something', down: 'immune system is quieter' },
    'white blood cell count': { up: 'your body may be fighting something', down: 'immune system is quieter' },
    'wbc': { up: 'your body may be fighting something', down: 'immune system is quieter' },
    'platelets': { up: 'blood may clot more easily', down: 'may bruise or bleed more easily' },
    'platelet count': { up: 'blood may clot more easily', down: 'may bruise or bleed more easily' },
    'c-reactive protein': { up: 'sign of inflammation somewhere', down: 'inflammation going down' },
    'crp': { up: 'sign of inflammation somewhere', down: 'inflammation going down' },
    'psa': { up: 'mention it to your doctor next visit', down: 'trending in a good direction' },
    'prostate specific antigen': { up: 'mention it to your doctor next visit', down: 'trending in a good direction' },
    'bilirubin, total': { up: 'liver processing may be off', down: 'liver processing looks fine' },
    'bilirubin': { up: 'liver processing may be off', down: 'liver processing looks fine' },
    'carbon dioxide': { up: 'acid-base balance shifted a bit', down: 'usually not a big deal on its own' },
  };

  const context = ctx[testKey]?.[direction] || '';
  let favorable: boolean;
  if (AMBIGUOUS.has(testKey)) {
    favorable = false;
  } else if (UP_IS_GOOD.has(testKey)) {
    favorable = direction === 'up';
  } else {
    favorable = direction === 'down';
  }
  return { context, favorable };
}
